import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { useSEO } from '../hooks/useSEO';
import { getEvidenceById } from '../lib/evidence';
import { DOMAIN_PAGES } from '../lib/site';
import { domainBlocksFor, domainConfigFor, domainContentFor } from '../lib/domainContent';

const brucki = getEvidenceById('brucki-2003');
const REFERENCES = [`${brucki.citation} DOI: ${brucki.primarySource.value}.`];

const RELATED = [
  { to: '/escolaridade', label: 'Mini Mental por escolaridade', note: 'veja como a escolaridade altera o desempenho esperado em todos os domínios.' },
  { to: '/interpretacao-dos-resultados', label: 'Como interpretar o resultado', note: 'leia a pontuação total junto com o desempenho por domínio, não isoladamente.' },
  { to: '/como-aplicar', label: 'Como aplicar o Mini Mental', note: 'a padronização da aplicação é o que torna este domínio comparável entre pacientes.' }
];

function DomainNotFound() {
  useSEO({ title: 'Domínio não encontrado — MiniMental.com.br', noindex: true });
  return (
    <main className="mx-auto max-w-[680px] px-5 py-14">
      <h1 className="mb-4 text-2xl font-semibold text-[var(--primary)]">Domínio não encontrado</h1>
      <p className="text-[var(--text-muted)]">
        Veja os <Link to="/guias" className="text-[var(--primary)] underline">guias do Mini Mental</Link> ou volte para a{' '}
        <Link to="/" className="text-[var(--primary)] underline">calculadora</Link>.
      </p>
    </main>
  );
}

export default function DominioMeem() {
  const { slug } = useParams();
  const page = DOMAIN_PAGES.find((p) => p.path === `/dominio/${slug}`);
  const config = page ? domainConfigFor(page.domainId) : null;
  const content = page ? domainContentFor(page.domainId) : null;
  const blocks = page ? domainBlocksFor(page.domainId) : [];

  useEffect(() => {
    if (page && config && content) window.eidocAnalytics?.capture('assessment_educational_page_viewed', {});
  }, [page, config, content]);

  if (!page || !config || !content) return <DomainNotFound />;

  const answer = `${config.help} ${content.relevancia}`;

  return (
    <ArticleLayout page={page} answer={answer} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="o-que-avalia">
        <h2 id="o-que-avalia" className="text-2xl mb-5">O que o bloco de {page.breadcrumb.toLowerCase()} avalia?</h2>
        <p className="text-[var(--text-muted)] mb-5">{config.help} Vale até {config.max} {config.max === 1 ? 'ponto' : 'pontos'} na pontuação total de 30.</p>
        <p className="text-[var(--text-muted)]">{content.explicacaoProcedimento}</p>
      </section>

      {blocks.length > 0 && (
        <section aria-labelledby="desempenho-saudaveis">
          <h2 id="desempenho-saudaveis" className="text-2xl mb-5">Como pessoas saudáveis pontuam aqui?</h2>
          <p className="text-[var(--text-muted)] mb-5">
            Dados de {brucki.subitemSampleSize} indivíduos saudáveis avaliados por subitem no estudo de Brucki et al. (2003):
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse border border-[var(--border)] rounded-xl overflow-hidden">
              <thead className="bg-[var(--surface-soft)]">
                <tr>
                  <th className="p-4 border border-[var(--border)] font-bold">Bloco</th>
                  <th className="p-4 border border-[var(--border)] font-bold text-right">Máximo</th>
                  <th className="p-4 border border-[var(--border)] font-bold text-right">Mediana saudáveis</th>
                  <th className="p-4 border border-[var(--border)] font-bold text-right">Média (DP)</th>
                </tr>
              </thead>
              <tbody>
                {blocks.map((block) => (
                  <tr key={block.id}>
                    <td className="p-4 border border-[var(--border)]">{block.label}</td>
                    <td className="p-4 border border-[var(--border)] text-right font-bold">{block.max}</td>
                    <td className="p-4 border border-[var(--border)] text-right">{String(block.normativo.mediana).replace('.', ',')}</td>
                    <td className="p-4 border border-[var(--border)] text-right text-[var(--text-muted)]">{String(block.normativo.media).replace('.', ',')} ({String(block.normativo.dp).replace('.', ',')})</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {blocks.map((block) => (
            <p key={block.id} className="text-[var(--text-muted)] mt-5">{block.nota}</p>
          ))}
        </section>
      )}

      <section aria-labelledby="limitacoes">
        <h2 id="limitacoes" className="text-2xl mb-5">Limitações da leitura por este domínio isolado</h2>
        <ul className="space-y-3 text-[var(--text-muted)] list-disc pl-5">
          {content.limitacoes.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </section>
    </ArticleLayout>
  );
}
